import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

// Format date function for consistent date display
function formatDateForInward(input) {
  if (!input) return "Not provided";

  try {
    const date = new Date(input); // works with ISO strings and timestamps
    if (isNaN(date.getTime())) return input;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return input;
  }
}

const handleExportExcelInward = async (filteredData, project) => {
  try {
    // Check if data exists
    if (!filteredData || filteredData.length === 0) {
      console.error("No data available to export");
      return;
    }

    // Determine if project is a single project object or an array of projects
    const isSingleProject =
      typeof project === "object" &&
      project !== null &&
      "projectName" in project &&
      !Array.isArray(project);

    // Get project name for display
    const projectNameForDisplay = isSingleProject
      ? project.projectName
      : "All Sites";

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Inward Entries");

    // Define headers
    const columns = [
      { header: "Project Name", key: "Project Name", width: 25 },
      { header: "Referral PO No.", key: "Referral PO No.", width: 20 },
      {
        header: "Material Requisition Note (MRN)",
        key: "Material Requisition Note (MRN)",
        width: 30,
      },
      { header: "Vehicle No.", key: "Vehicle No.", width: 20 },
      { header: "Invoice No.", key: "Invoice No.", width: 20 },
      { header: "Invoice Date", key: "Invoice Date", width: 15 },
      { header: "Gate Pass No.", key: "Gate Pass No.", width: 20 },
      { header: "Gate Pass Date", key: "Gate Pass Date", width: 15 },
      { header: "DC No.", key: "DC No.", width: 20 },
      { header: "DC Date", key: "DC Date", width: 15 },
      { header: "Supplier/Vendor", key: "Supplier/Vendor", width: 20 },
      { header: "Total Items", key: "Total Items", width: 15 },
      { header: "Done By", key: "Done By", width: 20 },
      { header: "Item Category", key: "Item Category", width: 20 },
      { header: "Item Description", key: "Item Description", width: 30 },
      { header: "Opening Stock", key: "Opening Stock", width: 20 },
      { header: "Quantity Accepted", key: "Quantity Accepted", width: 20 },
      { header: "Closing Stock", key: "Closing Stock", width: 20 },
      { header: "UOM", key: "UOM", width: 10 },
      { header: "Item Remark", key: "Item Remark", width: 30 },
      { header: "Item Image", key: "Item Image", width: 30 },
      { header: "Invoice Image", key: "Invoice Image", width: 30 },
    ];

    worksheet.columns = columns;

    // Style headers
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } }; // White text
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 30;

    // Apply background color to header row
    assignBackgroundColorToRow(headerRow, "FF4472C4"); // Blue background for header

    // Counter for current row number
    let rowNumber = 2;

    for (const entry of filteredData) {
      if (!entry) continue; // Skip undefined or null entries

      // Check if this is a request-converted record (has dueDate property)
      const isRequestRecord = entry && "dueDate" in entry;

      // Determine project name - using consistent approach for both entry types
      let entryProjectName = "";
      if (isSingleProject) {
        // If it's a single project view, use that project name
        entryProjectName = project.projectName;
      } else if (entry.projectName) {
        // If entry has its own project name
        entryProjectName = entry.projectName;
      } else if (isRequestRecord && project && project.projectName) {
        // If request record and we know the project name
        entryProjectName = project.projectName;
      } else {
        entryProjectName = "Not provided";
      }

      // Get the total number of items for counting
      let totalItems = 0;

      // Get the "Done By" info
      let doneBy = "";

      // Get timestamp for sorting request history
      let timestamp = isRequestRecord ? entry.id : entry.timeStamp;

      // Find the most recent request history entry
      if (entry.requestHistory) {
        const historyKeys = Object.keys(entry.requestHistory || {}).sort(
          (a, b) => parseInt(b) - parseInt(a)
        );
        if (historyKeys.length > 0) {
          const latestHistory = entry.requestHistory[historyKeys[0]];
          doneBy = isRequestRecord
            ? latestHistory.name ||
              latestHistory.doneBy ||
              entry.userName ||
              "Not provided"
            : latestHistory.doneBy || "Not provided";
        }
      } else {
        doneBy = entry.userName || "Not provided";
      }

      // Prepare base row data - make consistent between both types
      let baseRowData = {
        "Project Name": entryProjectName,
        "Referral PO No.": isRequestRecord
          ? entry.key || "Not provided"
          : entry.fields?.["01"]?.value || "Not provided",
        "Material Requisition Note (MRN)": isRequestRecord
          ? entry.mrnId?.toString() || "Not provided"
          : entry.fields?.["02"]?.value || "Not provided",
        "Vehicle No.": isRequestRecord
          ? entry.receivedData?.metadata?.vehicleNo || "Not provided"
          : entry.fields?.["03"]?.value || "Not provided",
        "Invoice No.": isRequestRecord
          ? entry.id?.toString() || "Not provided"
          : entry.fields?.["04"]?.["01"]?.value || "Not provided",
        "Invoice Date": isRequestRecord
          ? entry.receivedData?.metadata?.receivedDate
            ? formatDateForInward(entry.receivedData.metadata.receivedDate)
            : "Not provided"
          : (() => {
              const invoices = entry?.invoices;
              const firstInvoice = invoices ? Object.values(invoices)[0] : null;
              const invoiceItems = firstInvoice?.invoiceItems;
              const firstItem = invoiceItems
                ? Object.values(invoiceItems)[0]
                : null;
              const dateString =
                firstItem?.date || entry.fields?.["04"]?.["02"]?.value;
              return formatDateForInward(dateString);
            })(),
        "Gate Pass No.": isRequestRecord
          ? entry.receivedData?.metadata?.gatePassNo || "Not provided"
          : entry.fields?.["05"]?.["01"]?.value || "Not provided",
        "Gate Pass Date": isRequestRecord
          ? "Not provided" // Gate Pass Date is not stored in request records
          : entry.fields?.["05"]?.["02"]?.value
          ? formatDateForInward(entry.fields?.["05"]?.["02"]?.value)
          : "Not provided",
        "DC No.": isRequestRecord
          ? entry.receivedData?.metadata?.dcNumber || "Not provided"
          : entry.fields?.["06"]?.["01"]?.value || "Not provided",
        "DC Date": isRequestRecord
          ? "Not provided" // DC Date is not stored in request records
          : entry.fields?.["06"]?.["02"]?.value
          ? formatDateForInward(entry.fields?.["06"]?.["02"]?.value)
          : "Not provided",
        "Supplier/Vendor": isRequestRecord
          ? entry.userName || "Not provided"
          : entry.fields?.["07"]?.value || "Not provided",
        "Total Items": "0", // Will be updated below
        "Done By": doneBy || "Not provided",
      };

      // Process items and update total items
      if (isRequestRecord) {
        // For request-converted records
        let itemsToProcess = [];

        // Handle both array and object formats of items
        if (entry.items) {
          if (Array.isArray(entry.items)) {
            itemsToProcess = entry.items;
            totalItems = itemsToProcess.length;
          } else if (typeof entry.items === "object") {
            // Convert object to array of values
            itemsToProcess = Object.values(entry.items);
            totalItems = itemsToProcess.length;
          }
        }

        // Update the total items count
        baseRowData["Total Items"] = totalItems.toString();

        if (itemsToProcess.length === 0) {
          // If no items, add a single row with base data
          const rowData = { ...baseRowData };
          rowData["Item Category"] = "Not provided";
          rowData["Item Description"] = "Not provided";
          rowData["Opening Stock"] = "NA";
          rowData["Quantity Accepted"] = "Not provided";
          rowData["Closing Stock"] = "NA";
          rowData["UOM"] = "Not provided";
          rowData["Item Remark"] = "Not provided";
          rowData["Item Image"] = "No Image";
          rowData["Invoice Image"] = "No Image";

          const mainRow = worksheet.addRow(rowData);
          assignBackgroundColorToRow(mainRow, "FFD9E1F2"); // Light blue for data rows
          formatCellsWithLinks(mainRow);
          rowNumber++;
        } else {
          // Process each item
          itemsToProcess.forEach((item, itemIndex) => {
            const rowData =
              itemIndex === 0
                ? { ...baseRowData }
                : Object.fromEntries(
                    Object.keys(baseRowData).map((key) => [key, ""])
                  );

            rowData["Item Category"] = item.category || "Not provided";
            rowData["Item Description"] = item.description || "Not provided";

            // Handle opening stock - use "NA" if not present
            if (item.openingStock !== undefined && item.openingStock !== null) {
              const openingStock = item.openingStock || 0;
              rowData["Opening Stock"] = openingStock.toString();

              // Handle quantity accepted
              const quantityAccepted = parseFloat(item.quantity) || 0;
              rowData["Quantity Accepted"] = quantityAccepted.toString();

              // Calculate closing stock (opening stock + quantity accepted)
              const closingStock = parseFloat(openingStock) + quantityAccepted;
              rowData["Closing Stock"] = closingStock.toString();
            } else {
              rowData["Opening Stock"] = "NA";
              rowData["Quantity Accepted"] = item.quantity || "0";
              rowData["Closing Stock"] = "NA";
            }

            rowData["UOM"] = item.unit || "Not provided";
            rowData["Item Remark"] = item.remark || "No Remark";

            // Handle item images
            let itemImages = [];
            if (item.images) {
              // Handle both array and object formats for images
              if (Array.isArray(item.images)) {
                // Images is already an array of URLs
                itemImages = item.images.map((url) => ({ url }));
              } else if (typeof item.images === "object") {
                // Images is an object, extract values
                itemImages = Object.values(item.images).filter(
                  (img) => img.uri || img.url
                );
              }
            }

            if (itemImages.length > 0) {
              const itemLinks = itemImages.map((img, index) => ({
                text: `Image${index + 1}`,
                hyperlink: img.uri || img.url || img,
              }));

              rowData["Item Image"] = itemLinks;
            } else {
              rowData["Item Image"] = "No Image";
            }

            // Handle entry-level images as invoice image for first item
            if (itemIndex === 0) {
              // Handle entry-level images that might be an array
              let entryImages = [];

              if (entry.images) {
                if (Array.isArray(entry.images)) {
                  // Convert array of URLs to array of objects with url property
                  entryImages = entry.images.map((url) => ({ url }));
                } else if (typeof entry.images === "object") {
                  // Images is an object, extract values that have urls
                  entryImages = Object.values(entry.images).filter(
                    (img) => img && (img.uri || img.url)
                  );
                }
              }

              const signature = entry.receivedData?.metadata?.signature
                ? [{ uri: entry.receivedData.metadata.signature }]
                : [];

              const allEntryImages = [...entryImages, ...signature];

              if (allEntryImages.length > 0) {
                const invoiceLinks = allEntryImages.map((img, index) => ({
                  text: `Image${index + 1}`,
                  hyperlink: img.uri || img.url || img,
                }));
                rowData["Invoice Image"] = invoiceLinks;
              } else {
                rowData["Invoice Image"] = "No Image";
              }
            } else {
              rowData["Invoice Image"] = "";
            }

            const mainRow = worksheet.addRow(rowData);

            if (itemIndex === 0) {
              assignBackgroundColorToRow(mainRow, "FFD9E1F2"); // Light blue for data rows (uniformity)
            } else {
              assignBackgroundColorToRow(mainRow, "FFD9E1F2"); // Light blue for data rows
            }
            formatCellsWithLinks(mainRow);
            rowNumber++;
          });
        }
      } else {
        // For regular inward entries
        let invoiceImagesAtRoot = [];

        // Count total items for regular records
        if (entry.invoices) {
          Object.values(entry.invoices).forEach((invoice) => {
            if (invoice.invoiceItems) {
              totalItems += Object.keys(invoice.invoiceItems).length;
            }
          });
        }

        // Update the total items count
        baseRowData["Total Items"] = totalItems.toString();

        // Handle both array and object formats for entry-level images
        if (entry.images) {
          if (Array.isArray(entry.images)) {
            // Images is an array of URLs, convert to objects with url property
            invoiceImagesAtRoot = entry.images.map((url) => ({ url }));
          } else {
            // Images is an object, extract values
            invoiceImagesAtRoot = Object.values(entry.images).filter(
              (img) => img && img.url
            );
          }
        }

        // Check if there are invoices
        const invoices = entry.invoices || {};
        const invoiceKeys = Object.keys(invoices);

        if (invoiceKeys.length === 0) {
          // If no invoices, add a single row with base data
          const rowData = { ...baseRowData };
          rowData["Item Category"] = "Not provided";
          rowData["Item Description"] = "Not provided";
          rowData["Opening Stock"] = "NA";
          rowData["Quantity Accepted"] = "Not provided";
          rowData["Closing Stock"] = "NA";
          rowData["UOM"] = "Not provided";
          rowData["Item Remark"] = "Not provided";
          rowData["Item Image"] = "No Image";

          // Add invoice images if available at root level
          if (invoiceImagesAtRoot.length > 0) {
            const invoiceLinks = invoiceImagesAtRoot.map((img, index) => ({
              text: `Image${index + 1}`,
              hyperlink: img.url || img,
            }));
            rowData["Invoice Image"] = invoiceLinks;
          } else {
            rowData["Invoice Image"] = "No Image";
          }

          const mainRow = worksheet.addRow(rowData);
          assignBackgroundColorToRow(mainRow, "FFD9E1F2"); // Light blue for data rows (for uniformity)
          formatCellsWithLinks(mainRow);
          rowNumber++;
        } else {
          // Process each invoice
          let isFirstInvoice = true;

          invoiceKeys.forEach((invoiceKey) => {
            const invoice = invoices[invoiceKey];

            let invoiceImages = [];
            if (invoice.images) {
              if (Array.isArray(invoice.images)) {
                // Handle array of image URLs
                invoiceImages = invoice.images.map((url) => ({ url }));
              } else {
                // Handle object of images
                invoiceImages = Object.values(invoice.images).filter(
                  (img) => img && (img.uri || img.url)
                );
              }
            }
            invoiceImages = [...invoiceImagesAtRoot, ...invoiceImages];

            const invoiceItems = invoice.invoiceItems || {};
            const itemKeys = Object.keys(invoiceItems);

            if (itemKeys.length === 0) {
              // If no items in this invoice, add a row with invoice data
              const rowData = { ...baseRowData };
              rowData["Item Category"] = "Not provided";
              rowData["Item Description"] = "Not provided";
              rowData["Opening Stock"] = "NA";
              rowData["Quantity Accepted"] = "Not provided";
              rowData["Closing Stock"] = "NA";
              rowData["UOM"] = "Not provided";
              rowData["Item Remark"] = "Not provided";
              rowData["Item Image"] = "No Image";

              if (invoiceImages.length > 0) {
                const invoiceLinks = invoiceImages.map((img, index) => ({
                  text: `Image${index + 1}`,
                  hyperlink: img.uri || img.url || img,
                }));
                rowData["Invoice Image"] = invoiceLinks;
              } else {
                rowData["Invoice Image"] = "No Image";
              }

              const mainRow = worksheet.addRow(rowData);
              assignBackgroundColorToRow(
                mainRow,
                "FFD9E1F2" // Light blue for data rows (for uniformity)
              );
              formatCellsWithLinks(mainRow);
              rowNumber++;
              isFirstInvoice = false;
            } else {
              // Process each item in this invoice
              itemKeys.forEach((itemKey, itemIndex) => {
                const item = invoiceItems[itemKey];

                // Only display base row data for the first item of the first invoice
                const rowData =
                  isFirstInvoice && itemIndex === 0
                    ? { ...baseRowData }
                    : Object.fromEntries(
                        Object.keys(baseRowData).map((key) => [key, ""])
                      );

                rowData["Item Category"] = item.category || "Not provided";
                rowData["Item Description"] =
                  item.description || "Not provided";

                // Handle opening stock - use "NA" if not present
                if (
                  item.openingStock !== undefined &&
                  item.openingStock !== null
                ) {
                  const openingStock = item.openingStock || 0;
                  rowData["Opening Stock"] = openingStock.toString();

                  // Handle quantity accepted
                  const quantityAccepted =
                    parseFloat(item.quantityAccepted) || 0;
                  rowData["Quantity Accepted"] = quantityAccepted.toString();

                  // Calculate closing stock (opening stock + quantity accepted)
                  const closingStock =
                    parseFloat(openingStock) + quantityAccepted;
                  rowData["Closing Stock"] = closingStock.toString();
                } else {
                  rowData["Opening Stock"] = "NA";
                  rowData["Quantity Accepted"] = item.quantityAccepted || "0";
                  rowData["Closing Stock"] = "NA";
                }

                rowData["UOM"] = item.uom || "Not provided";
                rowData["Item Remark"] = item.remark || "No Remark";

                // Handling multiple item images
                let itemImages = [];
                if (item.images) {
                  if (Array.isArray(item.images)) {
                    // Handle array of image URLs
                    itemImages = item.images.map((url) => ({ url }));
                  } else {
                    // Handle object of images
                    itemImages = Object.values(item.images).filter(
                      (img) => img && (img.uri || img.url)
                    );
                  }
                }

                if (itemImages.length > 0) {
                  const itemLinks = itemImages.map((img, index) => ({
                    text: `Image${index + 1}`,
                    hyperlink: img.uri || img.url || img,
                  }));

                  rowData["Item Image"] = itemLinks;
                } else {
                  rowData["Item Image"] = "No Image";
                }

                // Handling multiple invoice images for first item only
                if (
                  isFirstInvoice &&
                  itemIndex === 0 &&
                  invoiceImages.length > 0
                ) {
                  const invoiceLinks = invoiceImages.map((img, index) => ({
                    text: `Image${index + 1}`,
                    hyperlink: img.uri || img.url || img,
                  }));

                  rowData["Invoice Image"] = invoiceLinks;
                } else {
                  rowData["Invoice Image"] = "";
                }

                const mainRow = worksheet.addRow(rowData);

                assignBackgroundColorToRow(mainRow, "FFD9E1F2"); // Light blue for all data rows (for uniformity)
                formatCellsWithLinks(mainRow);
                rowNumber++;
              });

              isFirstInvoice = false;
            }
          });
        }
      }
    }

    // Format cells with hyperlinks
    function formatCellsWithLinks(row) {
      row.eachCell((cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };

        // Set border for all cells
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        // Make header row text white
        if (row.number === 1) {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        } else if (
          cell.value === "" ||
          cell.value === null ||
          cell.value === undefined
        ) {
          // Format empty cells to prevent gaps
          cell.value = "";
        }

        // Check if cell contains image links array
        if (Array.isArray(cell.value)) {
          // Format the links
          const formattedLinks = cell.value.map((link) => {
            // Handle both object format and direct URL strings
            if (typeof link === "string") {
              return {
                text: "Image",
                hyperlink: link,
              };
            } else {
              return {
                text: link.text,
                hyperlink: link.hyperlink,
              };
            }
          });

          // Apply formatting to each link
          if (formattedLinks.length > 0) {
            cell.value = formattedLinks[0]; // Set first link
            cell.font = { color: { argb: "FF0000FF" }, underline: true };

            // Add remaining links in adjacent cells below
            if (formattedLinks.length > 1) {
              const colNumber = cell.col;
              formattedLinks.slice(1).forEach((link, index) => {
                const newCell = worksheet.getCell(
                  cell.row + index + 1,
                  colNumber
                );
                newCell.value = link;
                newCell.font = { color: { argb: "FF0000FF" }, underline: true };
                newCell.alignment = {
                  vertical: "middle",
                  horizontal: "left",
                  wrapText: true,
                };
              });
            }
          }
        }
      });
    }

    function assignBackgroundColorToRow(row, color) {
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: color },
        };
      });
    }

    // Auto-fit column widths based on content
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.value) {
          const cellValueLength = cell.value.toString().length;
          if (cellValueLength > maxLength) {
            maxLength = cellValueLength;
          }
        }
      });
      // Add some padding, ensure minimum width of 15 and max of 50
      column.width = Math.max(Math.min(maxLength + 2, 50), 15);
    });

    // Add filtering to the header row
    worksheet.autoFilter = {
      from: {
        row: 1,
        column: 1,
      },
      to: {
        row: 1,
        column: columns.length,
      },
    };

    // Freeze the header row so it stays visible when scrolling
    worksheet.views = [
      { state: "frozen", xSplit: 0, ySplit: 1, activeCell: "A2" },
    ];

    // Determine file name based on project
    let exportFileName = isSingleProject
      ? `${project.projectName}_Inward_${
          new Date().toISOString().split("T")[0]
        }.xlsx`
      : `AllSites_Inward_${new Date().toISOString().split("T")[0]}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), exportFileName);

    return true;
  } catch (error) {
    console.error("Error in handleExportExcelInward:", error);
    throw error;
  }
};

export default handleExportExcelInward;
